import { Box, TextField, Typography } from '@mui/material';
import { PaintBrush } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  INode,
  ITypeNode,
  useFormBuilder,
} from '../../Context/FormBuilderContext';
import { ActionTypes } from '../../Reducer';
import { TitleFormData } from '../../schemas/title.schema';
import { titleFormToNode, titleNodeToForm } from '../../utils/nodeDataTransformers';
import { CustomizationForm } from '../FormCustomization/CustomizationForm';

interface TitleSettingsProps {
  node: INode;
}

export function TitleSettings({ node }: TitleSettingsProps) {
  const { dispatch } = useFormBuilder();
  const [activeTab, setActiveTab] = useState(0);

  const formsMethods = useForm<TitleFormData>({
    defaultValues: titleNodeToForm(node),
  });

  useEffect(() => {
    formsMethods.reset(titleNodeToForm(node));
  }, [node.id, node.data, formsMethods]);

  useEffect(() => {
    const subscription = formsMethods.watch((data) => {
      if (data && Object.keys(data).length > 0) {
        const updatedData = titleFormToNode(data as TitleFormData, node);
        dispatch({
          type: ActionTypes.UPDATE_NODES_DATA,
          payload: {
            id: node.id,
            updates: { data: updatedData },
          },
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [formsMethods.watch, node.id, dispatch]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '16px 16px 0 16px',
        }}
      >
        <PaintBrush size={16} color="#101828" />
        <Typography
          style={{ fontSize: '14px', fontWeight: '500', color: '#101828' }}
        >
          Title Settings
        </Typography>
      </Box>

      <FormProvider {...formsMethods}>
        <form style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              gap: '4px',
              padding: '0px 16px 0 16px',
            }}
          >
            <Box
              onClick={() => setActiveTab(0)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 0 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 0 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 0 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Content
            </Box>

            <Box
              onClick={() => setActiveTab(1)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 1 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 1 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 1 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Design
            </Box>
          </Box>

          <div
            style={{ width: '100%', height: '1px', backgroundColor: '#EAECF0' }}
          />

          {activeTab === 0 && (
            <Box sx={{ padding: '0px 16px 0 16px' }}>
              <Controller
                name="title"
                control={formsMethods.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Title"
                    placeholder="Enter title..."
                    fullWidth
                    variant="outlined"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {
                        margin: '0px',
                        height: '38px',
                        fontSize: '14px',
                      },
                    }}
                  />
                )}
              />
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ padding: '0px 16px 0 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <CustomizationForm
                control={formsMethods.control}
                type={ITypeNode.TITLE}
              />
            </Box>
          )}
        </form>
      </FormProvider>
    </>
  );
}
